import React, { useCallback, useEffect } from 'react';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import styles from './styles.module.scss';
import ArrowUpIcon from '@/components/icons/ArrowUpIcon';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import KeyboardButton from '@/components/common/keyboard-button';
import buttonStyles from '@/components/common/keyboard-button/styles.module.scss';
import { useTranslation } from 'react-i18next';
import useBaseSize from '@/hooks/useBaseSize';
import { getHSize, getSize } from '@/utils/size';
import useKeyboardSimple from './useKeyboardSimple';
import { BuildingNameType } from '@/enums/building';
import { useConfigStore } from '@/stores';
import { useAccessibilityTTS } from '@/hooks';

interface KeyboardProps {
    tabIndex: number;
    value: string;
    keyboardLanguage: 'ko' | 'en';
    onLanguageChange?: (language: 'ko' | 'en') => void;
    onInputChange?: (value: string) => void;
    onSearch?: () => void;
    buildingType: BuildingNameType;
}

const KeyboardSimple: React.FC<KeyboardProps> = ({ value, onInputChange, onSearch, keyboardLanguage, onLanguageChange, tabIndex, buildingType }) => {
    const { t } = useTranslation();
    const { baseHeight, baseWidth } = useBaseSize();
    const voice = useConfigStore((state) => state?.config?.tts?.voice);
    const { speak } = useAccessibilityTTS();

    const {
        isKorean,
        isShiftPressed,
        setIsShiftPressed,
        keyboardRef,
        isSearchDisabled,
        rows,
        textLayout,
        displayMap,
        insertChar,
        handleKeyPress,
        // TTS
        getFocusTtsForKey,
        getEnterTtsForKey,
        speakIfEnabled,
    } = useKeyboardSimple({ value, keyboardLanguage, onInputChange, onLanguageChange });

    useEffect(() => {
        keyboardRef.current?.setInput(value ?? '');
    }, [value, keyboardRef]);

    // Helper function to add/remove active class directly to DOM element
    const addActiveClass = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const target = event.currentTarget;
        target.classList.add(buttonStyles['keyboard-button__active']);

        const timeout = setTimeout(() => {
            target.classList.remove(buttonStyles['keyboard-button__active']);
        }, 200);

        return () => clearTimeout(timeout);
    }, []);

    // Click handlers for different button types
    const handleNormalKeyClick = useCallback(
        (key: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
            addActiveClass(event);
            const next = insertChar(value, key, isKorean);
            keyboardRef.current?.setInput(next);
            onInputChange?.(next);
            const timeout = setTimeout(() => {
                speakIfEnabled(getEnterTtsForKey(key, next));
            }, 100);

            return () => clearTimeout(timeout);
        },
        [addActiveClass, insertChar, value, isKorean, keyboardRef, onInputChange, speakIfEnabled, getEnterTtsForKey]
    );

    const handleDeleteClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            addActiveClass(event);
            const next = value.slice(0, -1);
            keyboardRef.current?.setInput(next);
            onInputChange?.(next);

            if (!voice) {
                return;
            }

            const deletedNumber = value.length > 0 ? value[value.length - 1] : '';

            if (deletedNumber) {
                speak(`${deletedNumber} ${t('shop.keyboard.delete')} ${next}`);
            }
        },
        [addActiveClass, value, keyboardRef, onInputChange, voice]
    );

    const handleShiftClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            addActiveClass(event);
            setIsShiftPressed((s) => !s);
        },
        [addActiveClass, setIsShiftPressed]
    );

    const handleModeClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            addActiveClass(event);
            const newLanguage = isKorean ? 'en' : 'ko';
            onLanguageChange?.(newLanguage);
            setIsShiftPressed(false);
        },
        [addActiveClass, isKorean, onLanguageChange, setIsShiftPressed]
    );

    const handleSpaceClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            addActiveClass(event);
            const next = `${value} `;
            keyboardRef.current?.setInput(next);
            onInputChange?.(next);
        },
        [addActiveClass, value, keyboardRef, onInputChange]
    );

    const handleNumberClick = useCallback(
        (num: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
            addActiveClass(event);
            handleKeyPress(num);
            const next = keyboardRef.current?.getInput() ?? `${value}${num}`;
            const timeout = setTimeout(() => {
                speakIfEnabled(getEnterTtsForKey(num, next));
            }, 100);

            return () => clearTimeout(timeout);
        },
        [addActiveClass, handleKeyPress, keyboardRef, value, speakIfEnabled, getEnterTtsForKey]
    );

    const handleSearchClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (isSearchDisabled) return;

            addActiveClass(event);
            onSearch?.();
            speakIfEnabled(t('shop.keyboard.search'));
        },
        [addActiveClass, isSearchDisabled, onSearch, speakIfEnabled, t]
    );

    return (
        <div className={styles['keyboard']}>
            <div className={styles['keyboard__container']}>
                <div className={styles['keyboard__text']}>
                    <Keyboard
                        keyboardRef={(r) => (keyboardRef.current = r)}
                        onChange={(input) => onInputChange?.(input)}
                        onKeyPress={handleKeyPress}
                        layout={textLayout}
                        display={displayMap}
                        theme={styles['sk-hidden']}
                        disableButtonHold
                        physicalKeyboardHighlight={false}
                        layoutName={isShiftPressed ? 'shift' : 'default'}
                        input={value}
                    />

                    {/* Hàng 1 */}
                    <div className={styles['keyboard__text--row']} data-region-nav>
                        {rows.row1.map((key, index) => (
                            <KeyboardButton
                                data-tts-scope="keyboard"
                                key={`row1-${index}`}
                                title={key}
                                onFocus={() => {
                                    speakIfEnabled(getFocusTtsForKey(key));
                                }}
                                onClick={handleNormalKeyClick(key)}
                                tabIndex={tabIndex}
                                mode={isKorean ? 'ko' : 'en'}
                                width={'44'}
                                height={'42'}
                                aria-label={`${key} ${t('common.button.tts.button')}`}
                                data-interactive
                                keyboardType="normal"
                            />
                        ))}

                        <KeyboardButton
                            title={<ArrowLeftIcon width={getSize(16, baseWidth)} height={getHSize(16, baseHeight)} />}
                            onClick={handleDeleteClick}
                            tabIndex={tabIndex}
                            mode={isKorean ? 'ko' : 'en'}
                            width={'58'}
                            height={'42'}
                            aria-label={`${t('shop.keyboard.delete')}`}
                            data-interactive
                            keyboardType="delete"
                            role="button"
                            aria-checked={isKorean}
                        />
                    </div>

                    {/* Hàng 2 */}
                    <div className={styles['keyboard__text--row']} data-region-nav>
                        <KeyboardButton
                            title={<ArrowUpIcon width={getSize(16, baseWidth)} height={getHSize(16, baseHeight)} />}
                            onClick={handleShiftClick}
                            tabIndex={tabIndex}
                            mode={isKorean ? 'ko' : 'en'}
                            width={'58'}
                            height={'42'}
                            aria-label={`${t('shop.keyboard.shift')} ${t('common.button.tts.button')}`}
                            data-interactive
                            keyboardType="shift"
                            active={isShiftPressed}
                            role="switch"
                            aria-checked={isShiftPressed}
                        />

                        {rows.row2.map((key, index) => (
                            <KeyboardButton
                                data-tts-scope="keyboard"
                                key={`row2-${index}`}
                                title={key}
                                onFocus={() => {
                                    speakIfEnabled(getFocusTtsForKey(key));
                                }}
                                onClick={handleNormalKeyClick(key)}
                                tabIndex={tabIndex}
                                mode={isKorean ? 'ko' : 'en'}
                                width={'44'}
                                height={'42'}
                                aria-label={`${key} ${t('common.button.tts.button')}`}
                                data-interactive
                                keyboardType="normal"
                            />
                        ))}
                    </div>

                    {/* Hàng 3 */}
                    <div className={styles['keyboard__text--row']} data-region-nav>
                        <KeyboardButton
                            title={isKorean ? '한/영' : 'KO/EN'}
                            onClick={handleModeClick}
                            tabIndex={tabIndex}
                            mode={isKorean ? 'ko' : 'en'}
                            width={'84'}
                            height={'42'}
                            aria-label={`${t('shop.keyboard.mode')} ${t('common.button.tts.button')}`}
                            data-interactive
                            keyboardType="mode"
                            role="switch"
                            aria-checked={isKorean}
                        />
                        {rows.row3.map((key, index) => (
                            <KeyboardButton
                                data-tts-scope="keyboard"
                                key={`row3-${index}`}
                                title={key}
                                onFocus={() => {
                                    speakIfEnabled(getFocusTtsForKey(key));
                                }}
                                onClick={handleNormalKeyClick(key)}
                                tabIndex={tabIndex}
                                mode={isKorean ? 'ko' : 'en'}
                                width={'44'}
                                height={'42'}
                                aria-label={`${key} ${t('common.button.tts.button')}`}
                                data-interactive
                                keyboardType="normal"
                            />
                        ))}

                        <KeyboardButton
                            title={isKorean ? '띄어쓰기' : 'BLANK'}
                            onClick={handleSpaceClick}
                            tabIndex={tabIndex}
                            mode={isKorean ? 'ko' : 'en'}
                            width={'114'}
                            height={'42'}
                            aria-label={`${t('shop.keyboard.space')}`}
                            data-interactive
                            keyboardType="space"
                            role="button"
                        />
                    </div>
                </div>

                <div className={styles['keyboard__number']}>
                    <div data-region-nav>
                        <div className={styles['keyboard__number--row']}>
                            {['1', '2', '3', '4', '5'].map((num) => (
                                <KeyboardButton
                                    data-tts-scope="keyboard"
                                    key={`num1-${num}`}
                                    title={num}
                                    onFocus={() => {
                                        speakIfEnabled(getFocusTtsForKey(num));
                                    }}
                                    onClick={handleNumberClick(num)}
                                    tabIndex={tabIndex}
                                    mode={isKorean ? 'ko' : 'en'}
                                    buildingType={buildingType}
                                    width={'44'}
                                    height={'42'}
                                    aria-label={`${num} ${t('common.button.tts.button')}`}
                                    data-interactive
                                    keyboardType="normal"
                                    isNumber={true}
                                    className="number"
                                />
                            ))}
                        </div>
                        <div className={styles['keyboard__number--row']}>
                            {['6', '7', '8', '9', '0'].map((num) => (
                                <KeyboardButton
                                    data-tts-scope="keyboard"
                                    key={`num2-${num}`}
                                    title={num}
                                    onFocus={() => {
                                        speakIfEnabled(getFocusTtsForKey(num));
                                    }}
                                    onClick={handleNumberClick(num)}
                                    tabIndex={tabIndex}
                                    mode={isKorean ? 'ko' : 'en'}
                                    buildingType={buildingType}
                                    width={'44'}
                                    height={'42'}
                                    aria-label={`${num} ${t('common.button.tts.button')}`}
                                    data-interactive
                                    keyboardType="normal"
                                    isNumber={true}
                                    className="number"
                                />
                            ))}
                        </div>
                    </div>
                    <div className={styles['keyboard__number--search']}>
                        <KeyboardButton
                            data-tts-scope="keyboard"
                            data-region-nav
                            title={t('shop.keyboard.search')}
                            onClick={handleSearchClick}
                            tabIndex={tabIndex}
                            mode={isKorean ? 'ko' : 'en'}
                            width={'139'}
                            height={'42'}
                            data-interactive
                            keyboardType="search"
                            active={!isSearchDisabled}
                            onFocus={() => {
                                if (isSearchDisabled) {
                                    speakIfEnabled(`${t('shop.keyboard.search')} ${t('common.button.tts.inactive_button')}`);
                                } else {
                                    speakIfEnabled(`${t('shop.keyboard.search')} ${t('common.button.tts.button')}`);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KeyboardSimple;
